import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Modal,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  CtaLanguageDefinition,
  useCreateCtaLanguageMutation,
  useUpdateCtaLanguageMutation,
} from '~/features/dictionaries/model/api/cta-dictionary-api';

type CtaLanguageModalProps = {
  open: boolean;
  handleClose: () => void;
  editingLanguage?: CtaLanguageDefinition | null;
  onSaved?: () => void;
};

export const CtaLanguageModal = ({
  open,
  handleClose,
  editingLanguage,
  onSaved,
}: CtaLanguageModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [createLanguage, { isLoading: isCreating }] = useCreateCtaLanguageMutation();
  const [updateLanguage, { isLoading: isUpdating }] = useUpdateCtaLanguageMutation();
  const isLoading = isCreating || isUpdating;

  const [formState, setFormState] = useState({
    code: '',
    name: '',
    native_label: '',
    sort: '100',
    is_default: false,
    is_active: true,
  });

  useEffect(() => {
    setFormState({
      code: editingLanguage?.code ?? '',
      name: editingLanguage?.name ?? editingLanguage?.label ?? '',
      native_label: editingLanguage?.native_label ?? '',
      sort: String(editingLanguage?.sort ?? 100),
      is_default: editingLanguage?.is_default ?? false,
      is_active: editingLanguage?.is_active ?? true,
    });
  }, [editingLanguage, open]);

  const handleSave = async () => {
    const payload = {
      code: formState.code.trim(),
      name: formState.name.trim(),
      native_label: formState.native_label.trim(),
      sort: Number(formState.sort) || 0,
      is_default: formState.is_default,
      is_active: formState.is_default ? true : formState.is_active,
    };

    if (!payload.code || !payload.name || !payload.native_label) {
      enqueueSnackbar('Заполните код, название и native label языка.', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }

    try {
      if (editingLanguage?.id) {
        await updateLanguage({ id: editingLanguage.id, ...payload }).unwrap();
      } else {
        await createLanguage(payload).unwrap();
      }

      enqueueSnackbar('Язык CTA успешно сохранен.', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      handleClose();
      onSaved?.();
    } catch (error: any) {
      const validationErrors = error?.data?.errors;
      const validationMessage = validationErrors
        ? Object.values(validationErrors).flat().join('\n')
        : error?.data?.message || 'Не удалось сохранить язык CTA.';

      enqueueSnackbar(validationMessage, {
        variant: 'error',
        autoHideDuration: 5000,
        style: { maxWidth: '700px', whiteSpace: 'pre-wrap' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          width: 640,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          p: 3,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6">
            {editingLanguage ? 'Редактировать язык CTA' : 'Добавить язык CTA'}
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box flex={1}>
              <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
                Код языка
              </Typography>
              <TextField
                value={formState.code}
                onChange={e => setFormState(prev => ({ ...prev, code: e.target.value }))}
                disabled={isLoading || Boolean(editingLanguage?.id)}
                fullWidth
                placeholder="en-US"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Пример: en-US, de-DE, pl-PL
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
                Сортировка
              </Typography>
              <TextField
                type="number"
                value={formState.sort}
                onChange={e => setFormState(prev => ({ ...prev, sort: e.target.value }))}
                disabled={isLoading}
                fullWidth
              />
            </Box>
          </Stack>

          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              Название
            </Typography>
            <TextField
              value={formState.name}
              onChange={e => setFormState(prev => ({ ...prev, name: e.target.value }))}
              disabled={isLoading}
              fullWidth
            />
          </Box>
          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              Native label
            </Typography>
            <TextField
              value={formState.native_label}
              onChange={e => setFormState(prev => ({ ...prev, native_label: e.target.value }))}
              disabled={isLoading}
              fullWidth
            />
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControlLabel
              control={(
                <Switch
                  checked={formState.is_default}
                  onChange={e => setFormState(prev => ({
                    ...prev,
                    is_default: e.target.checked,
                    is_active: e.target.checked ? true : prev.is_active,
                  }))}
                  disabled={isLoading}
                />
              )}
              label="Язык по умолчанию"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={formState.is_active}
                  onChange={e => setFormState(prev => ({ ...prev, is_active: e.target.checked }))}
                  disabled={isLoading || formState.is_default}
                />
              )}
              label="Активный язык"
            />
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" onClick={handleClose} disabled={isLoading}>
              Отмена
            </Button>
            <Button fullWidth variant="contained" onClick={handleSave} disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> : null}
              Сохранить
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
